import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Activity } from 'lucide-react';
import { useState } from 'react';
import { ActivityFeed } from '@/components/activity-feed';
import { Page, PageHeader } from '@/components/page';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useDesks, useWorkspaceId } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

const ALL = '__all__';
const PAGE = 100;
const MAX = 1000;

export function ActivityPage() {
  const workspaceId = useWorkspaceId();
  const { deskId } = useSearch({ from: '/_authed/w/$workspaceId/activity' });
  const navigate = useNavigate({ from: '/w/$workspaceId/activity' });
  const desks = useDesks(workspaceId);
  const [limit, setLimit] = useState(PAGE);
  const deskNames = new Map((desks.data ?? []).map((d) => [d.id, d.name]));

  const events = useQuery({
    queryKey: qk.activity(workspaceId, deskId, limit),
    queryFn: () => api.workspaces.activity(workspaceId, { deskId, limit }),
    placeholderData: keepPreviousData,
  });

  return (
    <Page>
      <PageHeader
        title={t('Activity')}
        description={t('Everything that happens in this Workspace — newest first. Updates live.')}
        actions={
          <Select
            value={deskId ?? ALL}
            onValueChange={(v) => {
              setLimit(PAGE);
              void navigate({ search: { deskId: v === ALL ? undefined : v }, replace: true });
            }}
          >
            <SelectTrigger size="sm" className="w-56">
              <SelectValue placeholder={t('Desk')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('All desks')}</SelectItem>
              {(desks.data ?? []).map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      {events.isPending ? (
        <LoadingRows rows={8} />
      ) : events.isError ? (
        <ErrorState error={events.error} onRetry={() => void events.refetch()} />
      ) : events.data.length === 0 ? (
        <EmptyState
          icon={<Activity />}
          title={t('No activity yet')}
          description={deskId ? t('Nothing has happened on this Desk yet.') : t('As soon as Desks run and agents get to work, every step shows up here.')}
        />
      ) : (
        <Card className="px-5 py-1">
          <ActivityFeed events={events.data} workspaceId={workspaceId} deskNames={deskNames} />
          {events.data.length >= limit && limit < MAX && (
            <div className="border-t py-3 text-center">
              <Button variant="ghost" size="sm" onClick={() => setLimit((l) => l + PAGE)} loading={events.isFetching}>
                {t('Load older entries')}
              </Button>
            </div>
          )}
        </Card>
      )}
    </Page>
  );
}
